import React, {useMemo, useCallback} from 'react';
import styles from './RegionsTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {DataTable} from '../../shared';
import {RegionModel} from '../../entities';
import {Pencil, TrashBin, CaretUp, CaretDown} from '@gravity-ui/icons';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';

type SortProperty = 'name' | 'order';
type SortOrder = 'ascending' | 'descending';

interface RegionsTableProps {
  regions: RegionModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  onEditRegion?: (regionId: string) => void;
  onDeleteRegion?: (regionId: string) => void;
  sortProperty?: SortProperty;
  sortOrder?: SortOrder;
  onSortChange?: (property: SortProperty | undefined, order: SortOrder | undefined) => void;
}

export const RegionsTable = ({
  regions,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onEditRegion,
  onDeleteRegion,
  sortProperty,
  sortOrder = 'ascending',
  onSortChange,
}: RegionsTableProps) => {
  const SKELETON_ROWS = 5;

  const handleEditRegion = useCallback(
    (region: RegionModel) => {
      if (onEditRegion) {
        onEditRegion(region.id);
      }
    },
    [onEditRegion],
  );

  const handleDeleteRegion = useCallback(
    (region: RegionModel) => {
      if (onDeleteRegion) {
        onDeleteRegion(region.id);
      }
    },
    [onDeleteRegion],
  );

  const handleSort = useCallback(
    (property: SortProperty) => {
      if (onSortChange) {
        if (property === sortProperty) {
          const newOrder = sortOrder === 'ascending' ? 'descending' : 'ascending';
          onSortChange(property, newOrder);
        } else {
          onSortChange(property, 'ascending');
        }
      }
    },
    [sortProperty, sortOrder, onSortChange],
  );

  const handleResetSort = useCallback(() => {
    if (onSortChange) {
      onSortChange(undefined, undefined);
    }
  }, [onSortChange]);

  const renderSortIcon = useCallback(
    (property: SortProperty) => {
      if (property !== sortProperty) {
        return <Icon data={CaretDown} className={styles.sortIconInactive} />;
      }

      return sortOrder === 'ascending' ? (
        <Icon data={CaretUp} className={styles.sortIconActive} />
      ) : (
        <Icon data={CaretDown} className={styles.sortIconActive} />
      );
    },
    [sortProperty, sortOrder],
  );

  const columns = useMemo<TableColumnConfig<RegionModel>[]>(
    () => [
      {
        id: 'name',
        name: () => (
          <div className={styles.sortableHeader} onClick={() => handleSort('name')}>
            <Text variant="body-1">Название региона</Text>
            {renderSortIcon('name')}
          </div>
        ),

        template: (item: RegionModel) => (
          <div className={styles.cityCellContainer}>
            <Text variant="body-1">{item.name}</Text>
          </div>
        ),
      },
      {
        id: 'order',
        name: () => (
          <div className={styles.sortableHeader} onClick={() => handleSort('order')}>
            <Text variant="body-1">Порядок отображения</Text>
            {renderSortIcon('order')}
          </div>
        ),

        template: (item: RegionModel) => (
          <div className={styles.cityCellContainer}>
            <Text variant="body-1">{item.order}</Text>
          </div>
        ),
      },
      {
        id: 'more',
        name: () => (
          <div className={styles.tableHeader}>
            <Button view="normal" size="m" onClick={handleResetSort} className={styles.resetSortButton}>
              <Text variant="body-2">Сбросить сортировку</Text>
            </Button>
          </div>
        ),
        template: (item: RegionModel) => (
          <div className={styles.moreCell}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={Pencil} />,
                  action: () => handleEditRegion(item),
                  text: 'Редактировать',
                  className: styles.dropdownItem,
                },
                ...(item.usageCount === 0
                  ? [
                      {
                        iconStart: <Icon size={16} data={TrashBin} />,
                        action: () => handleDeleteRegion(item),
                        text: 'Удалить',
                        className: styles.deleteButton,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ),
      },
    ],
    [handleEditRegion, handleDeleteRegion, handleSort, renderSortIcon, handleResetSort],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<RegionModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={regions || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Регион не найден"
        emptyStateDescription="Регион с таким названием не найден. Попробуйте изменить запрос"
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
